import React, { useCallback, useEffect, useMemo, useState } from "react";
import { paymentService } from "../../apis/paymentService";
import { pricingService } from "../../apis/pricingService";
import { notifyPricingUpdated } from "../../utils/pricingDisplay";
import toast from "react-hot-toast";
import {
  RefreshCw,
  Loader2,
  DollarSign,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
} from "lucide-react";
import PaymentDateRangeModal, {
  DateRangeFilterButton,
} from "./PaymentDateRangeModal";

const STATUS_OPTIONS = [
  "",
  "pending",
  "processing",
  "completed",
  "failed",
  "refunded",
  "cancelled",
];

const PROCESSOR_OPTIONS = [
  { value: "stripe", label: "Stripe" },
  { value: "quickbooks", label: "QuickBooks" },
];

const paymentProcessorLabel = (payment) => {
  if (!payment) return "—";
  if (payment.payment_method === "stripe") return "Stripe";
  if (
    payment.payment_method === "quickbooks_hosted" ||
    payment.realm_id ||
    payment.payment_method === "card"
  ) {
    return "QuickBooks";
  }
  return payment.payment_method || "—";
};

const statusBadge = (status) => {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    processing: "bg-blue-100 text-blue-800",
    completed: "bg-teal-100 text-teal-800",
    failed: "bg-red-100 text-red-800",
    refunded: "bg-purple-100 text-purple-800",
    cancelled: "bg-gray-100 text-gray-800",
  };
  return map[status] || "bg-gray-100 text-gray-700";
};

const unwrap = (response) => {
  const payload = response?.data ?? response;
  if (payload?.body?.data) return payload.body.data;
  if (payload?.data) return payload.data;
  return payload;
};

const BILLING_PERIOD_OPTIONS = [
  { value: "", label: "All billing" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
];

const DEFAULT_PAYMENT_FILTERS = {
  status: "",
  email: "",
  customerName: "",
  processor: "stripe",
  planKey: "",
  billingPeriod: "",
  fromDate: "",
  toDate: "",
  sortBy: "created_at",
  sortOrder: "desc",
};

const SortHeader = ({ label, field, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  const Icon = active
    ? sortOrder === "asc"
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown;

  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1 hover:text-gray-900 ${
        active ? "text-gray-900 font-semibold" : ""
      }`}
    >
      {label}
      <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
    </button>
  );
};

export default function PaymentsAdmin() {
  const [activeTab, setActiveTab] = useState("payments");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingPaymentId, setSyncingPaymentId] = useState(null);
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_PAYMENT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_PAYMENT_FILTERS);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlan, setNewPlan] = useState({
    plan_key: "",
    billing_period: "monthly",
    display_name: "",
    description: "",
    amount: "",
    per_seat: false,
    min_seats: 1,
    max_seats: 100,
    is_active: true,
    sort_order: 0,
  });
  const [savingPlan, setSavingPlan] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [paymentsMessage, setPaymentsMessage] = useState("");
  const [syncConnected, setSyncConnected] = useState(false);
  const [qbTokenRealmId, setQbTokenRealmId] = useState(null);
  const [qbEnvRealmId, setQbEnvRealmId] = useState(null);
  const [qbStatus, setQbStatus] = useState("disconnected");
  const [qbMessage, setQbMessage] = useState("");
  const [qbWarnings, setQbWarnings] = useState([]);
  const [qbConfigLoading, setQbConfigLoading] = useState(true);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeMessage, setStripeMessage] = useState("");

  const emptyNewPlan = () => ({
    plan_key: "",
    billing_period: "monthly",
    display_name: "",
    description: "",
    amount: "",
    per_seat: false,
    min_seats: 1,
    max_seats: 100,
    is_active: true,
    sort_order: plans.length,
  });

  const loadStats = useCallback(async () => {
    try {
      const data = unwrap(await paymentService.getAdminStats());
      setStats(data?.stats || data);
    } catch (err) {
      toast.error(
        err.response?.status === 429
          ? "Too many requests — wait a moment and refresh"
          : "Failed to load payment stats"
      );
    }
  }, []);

  // silent: refresh the rows in place without swapping the table for the big
  // spinner, which collapses the page height and scrolls it back to the top.
  const loadPayments = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.email) params.email = appliedFilters.email;
      if (appliedFilters.customerName) params.customerName = appliedFilters.customerName;
      if (appliedFilters.processor) params.processor = appliedFilters.processor;
      if (appliedFilters.planKey) params.planKey = appliedFilters.planKey;
      if (appliedFilters.billingPeriod) {
        params.billingPeriod = appliedFilters.billingPeriod;
      }
      if (appliedFilters.fromDate) params.fromDate = appliedFilters.fromDate;
      if (appliedFilters.toDate) params.toDate = appliedFilters.toDate;
      if (appliedFilters.sortBy) params.sortBy = appliedFilters.sortBy;
      if (appliedFilters.sortOrder) params.sortOrder = appliedFilters.sortOrder;
      const data = unwrap(await paymentService.listAdminPayments(params));
      setPayments(data?.payments || []);
      setTotal(data?.total || 0);
    } catch (err) {
      toast.error(
        err.response?.status === 429
          ? "Too many requests — wait a moment and refresh"
          : err.response?.data?.body?.message || "Failed to load payments"
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, appliedFilters]);

  const loadPlans = useCallback(async () => {
    try {
      const data = unwrap(await pricingService.getAdminPlans());
      setPlans(data?.plans || []);
    } catch (err) {
      toast.error("Failed to load pricing plans");
    }
  }, []);

  const loadQbConnection = useCallback(async () => {
    setQbConfigLoading(true);
    try {
      const res = await paymentService.getConnectionConfig();
      const data = unwrap(res);
      setPaymentsEnabled(Boolean(data?.paymentsEnabled));
      setPaymentsMessage(data?.paymentsMessage || "");
      setSyncConnected(Boolean(data?.syncConnected ?? data?.connected));
      setStripeConnected(Boolean(data?.stripeConnected ?? data?.providers?.stripe?.enabled));
      setStripeMessage(
        data?.stripeMessage ||
          (data?.stripeConnected
            ? "Stripe API keys are configured."
            : "Stripe keys are not configured on the server.")
      );
      setQbTokenRealmId(data?.tokenRealmId || null);
      setQbEnvRealmId(data?.envRealmId || null);
      setQbStatus(data?.status || "disconnected");
      setQbMessage(data?.message || "");
      setQbWarnings(
        Array.isArray(data?.warnings) ? data.warnings.filter(Boolean) : []
      );
    } catch {
      setPaymentsEnabled(false);
      setPaymentsMessage("");
      setSyncConnected(false);
      setStripeConnected(false);
      setStripeMessage("");
      setQbTokenRealmId(null);
      setQbEnvRealmId(null);
      setQbStatus("disconnected");
      setQbMessage("");
      setQbWarnings([]);
    } finally {
      setQbConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadPayments();
    loadPlans();
    loadQbConnection();
  }, [loadStats, loadPayments, loadPlans, loadQbConnection]);

  const syncCanRun = useMemo(() => {
    if (appliedFilters.processor === "quickbooks") return syncConnected;
    return stripeConnected;
  }, [appliedFilters.processor, stripeConnected, syncConnected]);

  const syncProviderLabel = useMemo(() => {
    return appliedFilters.processor === "quickbooks" ? "QuickBooks" : "Stripe";
  }, [appliedFilters.processor]);

  const handleProviderChange = (value) => {
    setPage(1);
    setAppliedFilters((f) => ({ ...f, processor: value }));
    setDraftFilters((f) => ({ ...f, processor: value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      status: draftFilters.status,
      email: draftFilters.email,
      customerName: draftFilters.customerName,
      planKey: draftFilters.planKey,
      billingPeriod: draftFilters.billingPeriod,
    }));
  };

  const handleDateRangeApply = ({ fromDate, toDate }) => {
    setPage(1);
    setAppliedFilters((prev) => ({ ...prev, fromDate, toDate }));
  };

  const planKeyOptions = useMemo(() => {
    const seen = new Map();
    for (const plan of plans) {
      if (!plan.plan_key || seen.has(plan.plan_key)) continue;
      seen.set(
        plan.plan_key,
        plan.display_name?.replace(/\s*\([^)]*\)\s*$/, "").trim() || plan.plan_key
      );
    }
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [plans]);

  const handleSort = (field) => {
    setPage(1);
    const next = (f) => {
      if (f.sortBy === field) {
        return {
          ...f,
          sortOrder: f.sortOrder === "asc" ? "desc" : "asc",
        };
      }
      return {
        ...f,
        sortBy: field,
        sortOrder: field === "customer_name" ? "asc" : "desc",
      };
    };
    setAppliedFilters(next);
    setDraftFilters(next);
  };

  const handleClearFilters = () => {
    setPage(1);
    const cleared = {
      ...DEFAULT_PAYMENT_FILTERS,
      processor: appliedFilters.processor,
    };
    setAppliedFilters(cleared);
    setDraftFilters(cleared);
  };

  const hasClearableFilters = useMemo(() => {
    const isDefaultSort =
      appliedFilters.sortBy === DEFAULT_PAYMENT_FILTERS.sortBy &&
      appliedFilters.sortOrder === DEFAULT_PAYMENT_FILTERS.sortOrder;
    const appliedHasFilters =
      appliedFilters.status ||
      appliedFilters.email ||
      appliedFilters.customerName ||
      appliedFilters.planKey ||
      appliedFilters.billingPeriod ||
      appliedFilters.fromDate ||
      appliedFilters.toDate ||
      !isDefaultSort;
    const draftDiffers =
      draftFilters.status !== appliedFilters.status ||
      draftFilters.email !== appliedFilters.email ||
      draftFilters.customerName !== appliedFilters.customerName ||
      draftFilters.planKey !== appliedFilters.planKey ||
      draftFilters.billingPeriod !== appliedFilters.billingPeriod;
    return appliedHasFilters || draftDiffers;
  }, [appliedFilters, draftFilters]);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const data = unwrap(
        await paymentService.syncPendingPayments(appliedFilters.processor || "stripe")
      );
      toast.success(
        `Synced ${data?.synced || 0} payment(s), ${data?.failed || 0} failed`
      );
      await loadStats();
      await loadPayments({ silent: true });
    } catch (err) {
      const msg =
        err.response?.data?.body?.message ||
        err.response?.data?.message ||
        "Sync failed";
      toast.error(msg, { duration: 6000 });
      if (msg.toLowerCase().includes("quickbooks")) {
        loadQbConnection();
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncOne = async (payment) => {
    const processor = paymentProcessorLabel(payment).toLowerCase();
    if (processor === "stripe" && !stripeConnected) {
      toast.error("Configure Stripe API keys on the server to sync Stripe payments.", {
        duration: 6000,
      });
      return;
    }
    if (processor === "quickbooks" && !syncConnected) {
      toast.error(
        "Connect QuickBooks below to sync QuickBooks payments.",
        { duration: 6000 }
      );
      return;
    }
    setSyncingPaymentId(payment.id);
    try {
      const data = unwrap(await paymentService.syncPaymentStatus(payment.id));
      const status = data?.payment?.status;
      await loadStats();
      await loadPayments({ silent: true });
      toast.success(
        status ? `Payment status synced: ${status}` : "Payment status updated"
      );
    } catch (err) {
      const msg =
        err.response?.data?.body?.message ||
        err.response?.data?.message ||
        "Sync failed";
      toast.error(msg, { duration: 6000 });
      if (msg.toLowerCase().includes("quickbooks")) {
        loadQbConnection();
      }
    } finally {
      setSyncingPaymentId(null);
    }
  };

  const handleConnectQuickBooks = () => {
    paymentService.authorizeQuickBooks();
  };

  const handleMarkComplete = async (paymentId) => {
    try {
      await paymentService.markPaymentCompleted(paymentId);
      toast.success("Payment marked completed");
      await loadStats();
      await loadPayments();
    } catch (err) {
      toast.error("Failed to update payment");
    }
  };

  // const handleMarkRefund = async (paymentId) => {
  //   try {
  //     await paymentService.markPaymentRefunded(paymentId);
  //     toast.success("Payment marked refunded");
  //     await loadStats();
  //     await loadPayments();
  //   } catch (err) {
  //     toast.error("Failed to update payment");
  //   }
  // };

  const handleSavePlan = async () => {
    if (!editingPlan) return;
    setSavingPlan(true);
    try {
      await pricingService.updatePlan(editingPlan.id, {
        amount: parseFloat(editingPlan.amount),
        display_name: editingPlan.display_name,
        description: editingPlan.description || "",
        is_active: editingPlan.is_active,
        per_seat: editingPlan.per_seat,
        min_seats: editingPlan.min_seats,
        max_seats: editingPlan.max_seats,
        sort_order: editingPlan.sort_order,
      });
      toast.success("Pricing updated — changes apply everywhere immediately");
      setEditingPlan(null);
      await loadPlans();
      notifyPricingUpdated();
    } catch (err) {
      toast.error(err.response?.data?.body?.message || "Failed to save pricing");
    } finally {
      setSavingPlan(false);
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlan.plan_key?.trim() || !newPlan.display_name?.trim()) {
      toast.error("Plan key and display name are required");
      return;
    }
    if (newPlan.amount === "" || Number.isNaN(parseFloat(newPlan.amount))) {
      toast.error("Valid amount is required");
      return;
    }
    setCreatingPlan(true);
    try {
      await pricingService.createPlan({
        plan_key: newPlan.plan_key.trim(),
        billing_period: newPlan.billing_period,
        display_name: newPlan.display_name.trim(),
        description: newPlan.description?.trim() || "",
        amount: parseFloat(newPlan.amount),
        per_seat: newPlan.per_seat,
        min_seats: parseInt(newPlan.min_seats, 10) || 1,
        max_seats: parseInt(newPlan.max_seats, 10) || 100,
        is_active: newPlan.is_active,
        sort_order: parseInt(newPlan.sort_order, 10) || 0,
      });
      toast.success("Pricing plan created");
      setShowCreateForm(false);
      setNewPlan(emptyNewPlan());
      await loadPlans();
      notifyPricingUpdated();
    } catch (err) {
      toast.error(
        err.response?.data?.body?.message || "Failed to create pricing plan"
      );
    } finally {
      setCreatingPlan(false);
    }
  };

  const statCards = useMemo(
    () => [
      {
        label: "Total revenue",
        value: `$${Number(stats?.totalRevenue || 0).toLocaleString()}`,
        icon: <DollarSign className="w-5 h-5 text-green-600" />,
      },
      {
        label: "Pending",
        value: stats?.pending ?? 0,
        icon: <Clock className="w-5 h-5 text-amber-600" />,
      },
      {
        label: "Completed",
        value: stats?.completed ?? 0,
        icon: <CheckCircle2 className="w-5 h-5 text-teal-600" />,
      },
      {
        label: "Failed",
        value: stats?.failed ?? 0,
        icon: <AlertCircle className="w-5 h-5 text-red-600" />,
      },
    ],
    [stats]
  );

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payments & Pricing</h2>
          <p className="text-sm text-gray-600">
            Manage transactions and pricing. Filter by provider and sync pending payments from Stripe or QuickBooks.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {activeTab === "payments" && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <span className="font-medium whitespace-nowrap">Provider</span>
              <select
                value={appliedFilters.processor}
                onChange={(e) => handleProviderChange(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm font-medium bg-white min-w-[9.5rem]"
                aria-label="Payment provider"
              >
                {PROCESSOR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            onClick={() => {
              loadStats();
              loadPayments();
              loadPlans();
            }}
            className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {activeTab === "payments" && (
            <button
              type="button"
              onClick={handleSyncAll}
              disabled={syncing || !syncCanRun}
              title={
                !syncCanRun
                  ? `Connect ${syncProviderLabel} to enable sync`
                  : undefined
              }
              className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg text-sm hover:shadow-lg disabled:opacity-50"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              Sync pending
            </button>
          )}
        </div>
      </div>

      {!qbConfigLoading && (
        <div className="space-y-3">
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              paymentsEnabled
                ? "bg-green-50 border-green-200 text-green-900"
                : "bg-red-50 border-red-200 text-red-900"
            }`}
          >
            <p className="font-medium">
              Customer checkout: {paymentsEnabled ? "enabled" : "disabled"}
            </p>
            <p className="text-xs mt-1 opacity-90">
              Users can pay via Stripe (default) or QuickBooks hosted checkout when configured.
            </p>
            {!paymentsEnabled && paymentsMessage && (
              <p className="text-xs mt-2">{paymentsMessage}</p>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div
              className={`rounded-lg border px-4 py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 ${
                stripeConnected
                  ? "bg-green-50 border-green-200"
                  : "bg-amber-50 border-amber-200"
              }`}
            >
              <div className="text-sm space-y-2 min-w-0">
                <span
                  className={`font-medium ${
                    stripeConnected ? "text-green-900" : "text-amber-900"
                  }`}
                >
                  Stripe: {stripeConnected ? "connected" : "not configured"}
                </span>
                <p className="text-xs text-gray-700">
                  Used for card checkout and Stripe payment sync. Configure API keys in server environment.
                </p>
                {stripeMessage && (
                  <p className={stripeConnected ? "text-green-700 text-xs" : "text-amber-800 text-xs"}>
                    {stripeMessage}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={loadQbConnection}
                className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium ${
                  stripeConnected
                    ? "border border-green-600 text-green-800 hover:bg-green-100"
                    : "bg-amber-600 text-white hover:bg-amber-700"
                }`}
              >
                {stripeConnected ? "Refresh status" : "Check configuration"}
              </button>
            </div>

            <div
              className={`rounded-lg border px-4 py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 ${
                syncConnected
                  ? "bg-green-50 border-green-200"
                  : "bg-amber-50 border-amber-200"
              }`}
            >
              <div className="text-sm space-y-2 min-w-0">
                <div>
                  <span
                    className={`font-medium ${
                      syncConnected ? "text-green-900" : "text-amber-900"
                    }`}
                  >
                    QuickBooks:{" "}
                    {syncConnected ? "connected" : "not connected"}
                  </span>
                  {qbTokenRealmId && (
                    <span
                      className={
                        syncConnected ? "text-green-800 ml-2" : "text-amber-800 ml-2"
                      }
                    >
                      (OAuth realm {qbTokenRealmId})
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-700">
                  OAuth connection for QuickBooks hosted checkout and payment sync.
                </p>
                {qbMessage && (
                  <p className={syncConnected ? "text-green-700 text-xs" : "text-amber-800 text-xs"}>
                    {qbMessage}
                  </p>
                )}
                {qbEnvRealmId && qbEnvRealmId !== qbTokenRealmId && (
                  <p className="text-amber-900 text-xs">
                    Configured PAYMENTS_REALM_ID: {qbEnvRealmId}
                    {qbTokenRealmId
                      ? " — differs from OAuth token company."
                      : " — no OAuth token stored for this realm."}
                  </p>
                )}
                {qbStatus === "configured_missing_token" && (
                  <p className="text-amber-900 text-xs font-medium">
                    Realm ID is configured but admin OAuth was never completed.
                  </p>
                )}
                {qbWarnings.length > 0 && (
                  <ul className="list-disc pl-4 text-xs text-amber-900 space-y-1">
                    {qbWarnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={handleConnectQuickBooks}
                className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium ${
                  syncConnected
                    ? "border border-green-600 text-green-800 hover:bg-green-100"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {syncConnected ? "Reconnect QuickBooks" : "Connect QuickBooks"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b">
        {["payments", "pricing"].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            {tab === "payments" ? "Payment dashboard" : "Pricing plans"}
          </button>
        ))}
      </div>

      {activeTab === "payments" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white border rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{card.label}</span>
                  {card.icon}
                </div>
                <p className="text-2xl font-semibold mt-2">{card.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-3">
              Showing <span className="font-semibold text-gray-800">{syncProviderLabel}</span> payments
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex flex-wrap gap-3 items-end">
                <DateRangeFilterButton
                  fromDate={appliedFilters.fromDate}
                  toDate={appliedFilters.toDate}
                  onClick={() => setDateModalOpen(true)}
                />
                <label className="text-sm text-gray-600 flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Plan type</span>
                  <select
                    value={draftFilters.planKey}
                    onChange={(e) =>
                      setDraftFilters((f) => ({ ...f, planKey: e.target.value }))
                    }
                    className="border rounded-lg px-3 py-2 text-sm min-w-[9.5rem]"
                  >
                    <option value="">All plans</option>
                    {planKeyOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-gray-600 flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Billing</span>
                  <select
                    value={draftFilters.billingPeriod}
                    onChange={(e) =>
                      setDraftFilters((f) => ({
                        ...f,
                        billingPeriod: e.target.value,
                      }))
                    }
                    className="border rounded-lg px-3 py-2 text-sm min-w-[9.5rem]"
                  >
                    {BILLING_PERIOD_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-gray-600 flex flex-col gap-1 flex-1 min-w-[10rem]">
                  <span className="text-xs font-medium text-gray-500">Customer name</span>
                  <input
                    type="text"
                    placeholder="Search by name"
                    value={draftFilters.customerName}
                    onChange={(e) =>
                      setDraftFilters((f) => ({ ...f, customerName: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleApplyFilters();
                    }}
                    className="border rounded-lg px-3 py-2 text-sm w-full"
                  />
                </label>
                <label className="text-sm text-gray-600 flex flex-col gap-1 flex-1 min-w-[10rem]">
                  <span className="text-xs font-medium text-gray-500">Email</span>
                  <input
                    type="text"
                    placeholder="Search by email"
                    value={draftFilters.email}
                    onChange={(e) =>
                      setDraftFilters((f) => ({ ...f, email: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleApplyFilters();
                    }}
                    className="border rounded-lg px-3 py-2 text-sm w-full"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-3 items-center">
                <select
                  value={draftFilters.status}
                  onChange={(e) =>
                    setDraftFilters((f) => ({ ...f, status: e.target.value }))
                  }
                  className="border rounded-lg px-3 py-2 text-sm"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s || "all"} value={s}>
                      {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All statuses"}
                    </option>
                  ))}
                </select>
                <select
                  value={`${appliedFilters.sortBy}:${appliedFilters.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = e.target.value.split(":");
                    setPage(1);
                    const next = (f) => ({ ...f, sortBy, sortOrder });
                    setAppliedFilters(next);
                    setDraftFilters(next);
                  }}
                  className="border rounded-lg px-3 py-2 text-sm min-w-[11rem]"
                  aria-label="Sort payments"
                >
                  <option value="created_at:desc">Date · newest first</option>
                  <option value="created_at:asc">Date · oldest first</option>
                  <option value="customer_name:asc">Customer · A → Z</option>
                  <option value="customer_name:desc">Customer · Z → A</option>
                </select>
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm"
                >
                  Apply filters
                </button>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  disabled={!hasClearableFilters}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear all filters
                </button>
              </div>
            </div>

            <PaymentDateRangeModal
              open={dateModalOpen}
              onOpenChange={setDateModalOpen}
              fromDate={appliedFilters.fromDate}
              toDate={appliedFilters.toDate}
              onApply={handleDateRangeApply}
            />

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      <th className="py-2 pr-3">ID</th>
                      <th className="py-2 pr-3">
                        <SortHeader
                          label="Customer"
                          field="customer_name"
                          sortBy={appliedFilters.sortBy}
                          sortOrder={appliedFilters.sortOrder}
                          onSort={handleSort}
                        />
                      </th>
                      <th className="py-2 pr-3">Amount</th>
                      <th className="py-2 pr-3">Plan</th>
                      <th className="py-2 pr-3">Provider</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">
                        <SortHeader
                          label="Date"
                          field="created_at"
                          sortBy={appliedFilters.sortBy}
                          sortOrder={appliedFilters.sortOrder}
                          onSort={handleSort}
                        />
                      </th>
                      <th className="py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-gray-500">
                          No payments found
                        </td>
                      </tr>
                    ) : (
                      payments.map((p) => (
                        <tr key={p.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 pr-3 font-mono text-xs">{p.id}</td>
                          <td className="py-3 pr-3">
                            <div className="font-medium">{p.customer_name}</div>
                            <div className="text-xs text-gray-500">
                              {p.customer_email}
                            </div>
                          </td>
                          <td className="py-3 pr-3">${parseFloat(p.amount).toFixed(2)}</td>
                          <td className="py-3 pr-3 text-xs">
                            {p.plan_key || "—"}
                            {p.billing_period ? ` / ${p.billing_period}` : ""}
                            {p.seat_count > 1 ? ` (${p.seat_count} seats)` : ""}
                          </td>
                          <td className="py-3 pr-3 text-xs">
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                              {paymentProcessorLabel(p)}
                            </span>
                          </td>
                          <td className="py-3 pr-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(
                                p.status
                              )}`}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td className="py-3 pr-3 text-xs text-gray-600">
                            {new Date(p.created_at).toLocaleString()}
                          </td>
                          <td className="py-3">
                            <div className="flex flex-wrap gap-1">
                              <button
                                type="button"
                                onClick={() => handleSyncOne(p)}
                                disabled={syncingPaymentId !== null}
                                className="inline-flex items-center justify-center gap-1 min-w-[3.75rem] text-xs px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {syncingPaymentId === p.id ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Syncing
                                  </>
                                ) : (
                                  "Sync"
                                )}
                              </button>
                              {p.status === "pending" && (
                                <button
                                  type="button"
                                  onClick={() => handleMarkComplete(p.id)}
                                  className="text-xs px-2 py-1 border border-teal-300 text-teal-700 rounded hover:bg-teal-50"
                                >
                                  Complete
                                </button>
                              )}
                              {/* {p.status === "completed" && (
                                <button
                                  type="button"
                                  onClick={() => handleMarkRefund(p.id)}
                                  className="text-xs px-2 py-1 border border-purple-300 text-purple-700 rounded hover:bg-purple-50"
                                >
                                  Refund
                                </button>
                              )} */}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
              <span>
                Showing {payments.length} of {total}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1 border rounded disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page * 20 >= total}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1 border rounded disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === "pricing" && (
        <div className="bg-white border rounded-xl p-4 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              Create and edit plans — payment page, subscription modal, and
              marketing pricing load name, price, and description from the
              database.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm((v) => !v);
                setEditingPlan(null);
                if (!showCreateForm) setNewPlan(emptyNewPlan());
              }}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg text-sm hover:shadow-lg"
            >
              {showCreateForm ? "Cancel new plan" : "+ New pricing plan"}
            </button>
          </div>

          {showCreateForm && (
            <div className="p-4 border rounded-lg bg-blue-50/50 space-y-3">
              <h3 className="font-medium text-gray-900">Create pricing plan</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <label className="text-sm">
                  Plan key *
                  <input
                    className="mt-1 w-full border rounded px-3 py-2"
                    placeholder="e.g. starter, professional, elite"
                    value={newPlan.plan_key}
                    onChange={(e) =>
                      setNewPlan((p) => ({ ...p, plan_key: e.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  Billing period *
                  <select
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={newPlan.billing_period}
                    onChange={(e) =>
                      setNewPlan((p) => ({
                        ...p,
                        billing_period: e.target.value,
                      }))
                    }
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annually</option>
                  </select>
                </label>
                <label className="text-sm">
                  Display name *
                  <input
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={newPlan.display_name}
                    onChange={(e) =>
                      setNewPlan((p) => ({
                        ...p,
                        display_name: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm sm:col-span-2 lg:col-span-3">
                  Description
                  <textarea
                    rows={2}
                    className="mt-1 w-full border rounded px-3 py-2"
                    placeholder="Shown on pricing pages and checkout"
                    value={newPlan.description}
                    onChange={(e) =>
                      setNewPlan((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm">
                  Amount (USD) *
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={newPlan.amount}
                    onChange={(e) =>
                      setNewPlan((p) => ({ ...p, amount: e.target.value }))
                    }
                  />
                </label>
                <label className="text-sm">
                  Sort order
                  <input
                    type="number"
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={newPlan.sort_order}
                    onChange={(e) =>
                      setNewPlan((p) => ({
                        ...p,
                        sort_order: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={newPlan.per_seat}
                    onChange={(e) =>
                      setNewPlan((p) => ({
                        ...p,
                        per_seat: e.target.checked,
                      }))
                    }
                  />
                  Per seat pricing
                </label>
                {newPlan.per_seat && (
                  <>
                    <label className="text-sm">
                      Min seats
                      <input
                        type="number"
                        min="1"
                        className="mt-1 w-full border rounded px-3 py-2"
                        value={newPlan.min_seats}
                        onChange={(e) =>
                          setNewPlan((p) => ({
                            ...p,
                            min_seats: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="text-sm">
                      Max seats
                      <input
                        type="number"
                        min="1"
                        className="mt-1 w-full border rounded px-3 py-2"
                        value={newPlan.max_seats}
                        onChange={(e) =>
                          setNewPlan((p) => ({
                            ...p,
                            max_seats: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </>
                )}
                <label className="text-sm flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={newPlan.is_active}
                    onChange={(e) =>
                      setNewPlan((p) => ({
                        ...p,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                  Active (visible on site)
                </label>
              </div>
              <button
                type="button"
                onClick={handleCreatePlan}
                disabled={creatingPlan}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg text-sm hover:shadow-lg disabled:opacity-50"
              >
                {creatingPlan ? "Creating…" : "Create plan"}
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <th className="py-2 pr-2">Key</th>
                  <th className="py-2 pr-2">Display name</th>
                  <th className="py-2 pr-2">Description</th>
                  <th className="py-2">Period</th>
                  <th className="py-2">Amount</th>
                  <th className="py-2">Per seat</th>
                  <th className="py-2">Active</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id} className="border-b align-top">
                    <td className="py-3 pr-2 font-mono text-xs text-gray-600">
                      {plan.plan_key}
                    </td>
                    <td className="py-3 pr-2">{plan.display_name}</td>
                    <td className="py-3 pr-2 max-w-[200px] text-gray-600 text-xs">
                      {plan.description || "—"}
                    </td>
                    <td className="py-3 capitalize">{plan.billing_period}</td>
                    <td className="py-3">
                      ${parseFloat(plan.amount).toFixed(2)}
                    </td>
                    <td className="py-3">{plan.per_seat ? "Yes" : "No"}</td>
                    <td className="py-3">{plan.is_active ? "Yes" : "No"}</td>
                    <td className="py-3">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPlan({ ...plan });
                          setShowCreateForm(false);
                        }}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {editingPlan && (
            <div className="p-4 border rounded-lg bg-gray-50 space-y-3">
              <h3 className="font-medium">
                Edit: {editingPlan.display_name}{" "}
                <span className="text-gray-500 font-normal text-sm">
                  ({editingPlan.plan_key} · {editingPlan.billing_period})
                </span>
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-sm">
                  Display name
                  <input
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={editingPlan.display_name}
                    onChange={(e) =>
                      setEditingPlan((p) => ({
                        ...p,
                        display_name: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm">
                  Amount (USD)
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={editingPlan.amount}
                    onChange={(e) =>
                      setEditingPlan((p) => ({ ...p, amount: e.target.value }))
                    }
                  />
                </label>
                <label className="text-sm sm:col-span-2">
                  Description
                  <textarea
                    rows={3}
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={editingPlan.description || ""}
                    onChange={(e) =>
                      setEditingPlan((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm">
                  Sort order
                  <input
                    type="number"
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={editingPlan.sort_order ?? 0}
                    onChange={(e) =>
                      setEditingPlan((p) => ({
                        ...p,
                        sort_order: parseInt(e.target.value, 10) || 0,
                      }))
                    }
                  />
                </label>
                <label className="text-sm flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={editingPlan.per_seat}
                    onChange={(e) =>
                      setEditingPlan((p) => ({
                        ...p,
                        per_seat: e.target.checked,
                      }))
                    }
                  />
                  Per seat
                </label>
                <label className="text-sm flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={editingPlan.is_active}
                    onChange={(e) =>
                      setEditingPlan((p) => ({
                        ...p,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                  Active
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSavePlan}
                  disabled={savingPlan}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg text-sm hover:shadow-lg disabled:opacity-50"
                >
                  {savingPlan ? "Saving…" : "Save changes"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingPlan(null)}
                  className="px-4 py-2 border rounded-lg text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}